"use client";

import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import GetICMAppModal from "@/app/components/get-icm-app-modal";
import ArrowDownToLineIcon from "@/app/assets/icons/who-is-icm-for/arrow-down-to-line";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { DirectPaths } from "@/config/routes";
import {
    KNOW_MONEY_BETTER_SLIDES,
    type KnowMoneyBetterSlide,
    type KnowMoneyBetterVisualBg,
} from "@/app/stocks/icm 3.0/know-money-better-data";

const AUTO_SCROLL_MS = 5000;
const SLIDE_COUNT = KNOW_MONEY_BETTER_SLIDES.length;

const VISUAL_BG: Record<KnowMoneyBetterVisualBg, string> = {
    midnight: "bg-[#DEE8FB]",
    neutral: "bg-[#F5F5F5]",
};

const FeatureCheck = () => (
    <span className="shrink-0 text-base leading-[25.6px] text-[#16A34A]" aria-hidden>
        ✓
    </span>
);

const SlideCta = ({
    slide,
    className,
}: {
    slide: KnowMoneyBetterSlide;
    className?: string;
}) => {
    const buttonClass = cn(
        "inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[#283A97] bg-white px-6 py-2.5 text-[#283A97] transition-colors hover:bg-[#F8FAFF]",
        className,
    );

    if (slide.cta.type === "external") {
        return (
            <a
                href={slide.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(buttonClass, "text-sm font-medium leading-5 md:text-base md:leading-[22px]")}
            >
                {slide.cta.label}
                <GrowWealthChevron />
            </a>
        );
    }

    return (
        <TrackedLink
            href="/login"
            event={`Broking_KnowMoneyBetter_${slide.id}_CTA`}
            className={cn(buttonClass, "text-base font-medium leading-[22px] no-underline")}
        >
            {slide.cta.label}
            <GrowWealthChevron />
        </TrackedLink>
    );
};

const DesktopSlideCard = ({ slide }: { slide: KnowMoneyBetterSlide }) => (
    <article className="flex h-[503px] w-full items-center gap-6 overflow-hidden rounded-2xl border border-surface-stroke bg-white p-3">
        <div
            className={cn(
                "relative h-[479px] w-[466px] shrink-0 overflow-hidden rounded-xl p-6",
                VISUAL_BG[slide.visualBg],
            )}
        >
            <div className="absolute left-6 top-6 z-10 flex w-[418px] flex-col gap-3 text-left">
                <p className="text-2xl font-semibold leading-8 text-[#292929]">
                    {slide.visualTitle}
                </p>
                <p className="text-base font-regular leading-[22px] text-[#767676]">
                    {slide.visualDescription}
                </p>
            </div>
            <div className="absolute bottom-4 left-1/2 h-[276px] w-[323px] -translate-x-1/2">
                <Image
                    src={slide.illustrationDesktop}
                    alt=""
                    fill
                    className="object-contain object-bottom"
                    sizes="323px"
                />
            </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-10 py-1">
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                    <p className="text-xl font-semibold uppercase leading-[26px] tracking-[2px] text-[#F15D2A]">
                        {slide.overline}
                    </p>
                    <h3 className="text-[36px] font-bold leading-[44px] text-black">
                        {slide.title}
                    </h3>
                </div>
                <div className="flex max-w-[565px] flex-col gap-6">
                    <p className="max-w-[502px] text-lg font-regular leading-6 text-[#575757]">
                        {slide.description}
                    </p>
                    <ul className="flex flex-wrap gap-x-6 gap-y-3">
                        {slide.features.map((feature) => (
                            <li key={feature} className="flex items-center gap-1.5">
                                <FeatureCheck />
                                <span className="text-base font-medium leading-[22px] text-black">
                                    {feature}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <SlideCta slide={slide} />
        </div>
    </article>
);

const MobileSlideCard = ({ slide }: { slide: KnowMoneyBetterSlide }) => (
    <article className="flex w-full flex-col gap-6 overflow-hidden rounded-2xl border border-surface-stroke bg-white px-4 py-6">
        <div
            className={cn(
                "relative h-[379px] w-full overflow-hidden rounded-xl",
                VISUAL_BG[slide.visualBg],
            )}
        >
            <div className="absolute left-0 top-6 z-10 flex w-full flex-col gap-3 px-5 text-left">
                <p className="whitespace-pre-line text-xl font-bold leading-[26px] text-[#292929]">
                    {slide.visualTitle}
                </p>
                <p className="text-base font-regular leading-[22px] text-[#767676]">
                    {slide.visualDescription}
                </p>
            </div>
            <div className="absolute bottom-4 left-1/2 h-[172px] w-[200px] -translate-x-1/2">
                <Image
                    src={slide.illustrationMobile}
                    alt=""
                    fill
                    className="object-contain object-bottom"
                    sizes="200px"
                />
            </div>
        </div>

        <div className="flex w-full flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                    <p className="text-base font-semibold uppercase leading-[22px] tracking-[2px] text-[#F15D2A]">
                        {slide.overline}
                    </p>
                    <h3 className="text-2xl font-bold leading-8 text-black">{slide.title}</h3>
                </div>
                <p className="text-base font-regular leading-[22px] text-[#767676]">
                    {slide.description}
                </p>
                <ul className="flex flex-col gap-3">
                    {slide.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-1.5">
                            <FeatureCheck />
                            <span className="text-base font-medium leading-[22px] text-black">
                                {feature}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
            <SlideCta slide={slide} className="w-full" />
        </div>
    </article>
);

const CarouselDots = ({
    count,
    activeIndex,
    onSelect,
    autoplayActive,
}: {
    count: number;
    activeIndex: number;
    onSelect: (index: number) => void;
    autoplayActive: boolean;
}) => (
    <div
        className="flex items-center justify-center gap-[7px]"
        role="group"
        aria-label="Carousel pagination"
    >
        {Array.from({ length: count }, (_, index) => {
            const isActive = index === activeIndex;
            return (
                <button
                    key={index}
                    type="button"
                    aria-label={`Show slide ${index + 1}`}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => onSelect(index)}
                    className={cn(
                        "relative h-1.5 overflow-hidden rounded-full transition-[width] duration-300",
                        isActive ? "w-[18px] bg-[#D4D4D4]" : "w-1.5 bg-[#D4D4D4]",
                    )}
                >
                    {isActive ? (
                        <span
                            key={autoplayActive ? `fill-${activeIndex}` : "static"}
                            aria-hidden
                            className={cn(
                                "absolute inset-0 rounded-full bg-[#283980]",
                                autoplayActive
                                    ? "animate-know-money-better-pill-fill motion-reduce:animate-none motion-reduce:scale-x-100"
                                    : "scale-x-100 origin-left",
                            )}
                        />
                    ) : null}
                </button>
            );
        })}
    </div>
);

/**
 * Know Your Money Better — Figma web 8168:87480 / mobile 2541:36047
 */
const KnowMoneyBetter = () => {
    const [showAppModal, setShowAppModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [autoplayStopped, setAutoplayStopped] = useState(false);

    const [desktopRef, desktopApi] = useEmblaCarousel({
        align: "start",
        containScroll: "trimSnaps",
        dragFree: false,
    });
    const [mobileRef, mobileApi] = useEmblaCarousel({
        align: "start",
        containScroll: "trimSnaps",
        dragFree: false,
    });

    const stopAutoplay = useCallback(() => {
        setAutoplayStopped(true);
    }, []);

    const onDesktopSelect = useCallback(() => {
        if (!desktopApi) return;
        setSelectedIndex(desktopApi.selectedScrollSnap());
    }, [desktopApi]);

    const onMobileSelect = useCallback(() => {
        if (!mobileApi) return;
        setSelectedIndex(mobileApi.selectedScrollSnap());
    }, [mobileApi]);

    useEffect(() => {
        if (!desktopApi) return;
        onDesktopSelect();
        desktopApi.on("select", onDesktopSelect);
        desktopApi.on("reInit", onDesktopSelect);
        desktopApi.on("pointerDown", stopAutoplay);
        return () => {
            desktopApi.off("select", onDesktopSelect);
            desktopApi.off("reInit", onDesktopSelect);
            desktopApi.off("pointerDown", stopAutoplay);
        };
    }, [desktopApi, onDesktopSelect, stopAutoplay]);

    useEffect(() => {
        if (!mobileApi) return;
        onMobileSelect();
        mobileApi.on("select", onMobileSelect);
        mobileApi.on("reInit", onMobileSelect);
        mobileApi.on("pointerDown", stopAutoplay);
        return () => {
            mobileApi.off("select", onMobileSelect);
            mobileApi.off("reInit", onMobileSelect);
            mobileApi.off("pointerDown", stopAutoplay);
        };
    }, [mobileApi, onMobileSelect, stopAutoplay]);

    useEffect(() => {
        if (autoplayStopped) return;

        const id = window.setTimeout(() => {
            const next = (selectedIndex + 1) % SLIDE_COUNT;
            desktopApi?.scrollTo(next);
            mobileApi?.scrollTo(next);
        }, AUTO_SCROLL_MS);

        return () => window.clearTimeout(id);
    }, [autoplayStopped, selectedIndex, desktopApi, mobileApi]);

    const scrollTo = (index: number) => {
        stopAutoplay();
        desktopApi?.scrollTo(index);
        mobileApi?.scrollTo(index);
        setSelectedIndex(index);
    };

    const autoplayActive = !autoplayStopped;

    return (
        <section
            aria-labelledby="know-money-better-heading"
            className="mt-[60px] md:mt-[120px] w-full px-4 md:px-[120px]"
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 md:gap-[60px]">
                <header className="flex w-full flex-col items-center gap-1 text-center md:flex-row md:items-start md:justify-between md:gap-6 md:text-left">
                    <div className="flex w-full flex-col items-center gap-1 md:max-w-[937px] md:items-start md:gap-2">
                        <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-[#F15D2A] md:text-xl md:leading-[26px] md:tracking-[1.6px]">
                            KNOW YOUR MONEY BETTER
                        </p>
                        <h2
                            id="know-money-better-heading"
                            className="w-full text-2xl font-bold leading-8 text-black md:text-5xl md:leading-none"
                        >
                            <span className="md:hidden">
                                Smarter Tracking.
                                <br />
                                Better Investments.
                            </span>
                            <span className="hidden md:inline">
                                Smarter Tracking. Better Investments.
                            </span>
                        </h2>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            void setAnalyticsEvent(
                                DirectPaths.STOCKS,
                                "Broking_KnowMoneyBetter_Download_App",
                            );
                            setShowAppModal(true);
                        }}
                        className="hidden h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-[#283A97] bg-white px-6 py-2.5 text-base font-medium leading-[22px] text-[#283A97] transition-colors hover:bg-[#F8FAFF] md:inline-flex"
                    >
                        Download the app
                        <ArrowDownToLineIcon />
                    </button>
                </header>

                {/* Desktop carousel — peek next slide (Figma 8177:30689) */}
                <div className="hidden w-full flex-col gap-6 md:flex">
                    <div className="overflow-hidden" ref={desktopRef}>
                        <ul className="flex touch-pan-y">
                            {KNOW_MONEY_BETTER_SLIDES.map((slide) => (
                                <li
                                    key={slide.id}
                                    className="min-w-0 shrink-0 grow-0 basis-[calc(100%-97px)] pr-6"
                                >
                                    <DesktopSlideCard slide={slide} />
                                </li>
                            ))}
                        </ul>
                    </div>
                    <CarouselDots
                        count={KNOW_MONEY_BETTER_SLIDES.length}
                        activeIndex={selectedIndex}
                        onSelect={scrollTo}
                        autoplayActive={autoplayActive}
                    />
                </div>

                {/* Mobile carousel — Figma 2541:36133 */}
                <div className="flex w-full flex-col gap-6 md:hidden">
                    <div className="overflow-hidden" ref={mobileRef}>
                        <ul className="flex touch-pan-y">
                            {KNOW_MONEY_BETTER_SLIDES.map((slide) => (
                                <li
                                    key={slide.id}
                                    className="min-w-0 w-full shrink-0 grow-0 basis-full pr-4"
                                >
                                    <MobileSlideCard slide={slide} />
                                </li>
                            ))}
                        </ul>
                    </div>
                    <CarouselDots
                        count={KNOW_MONEY_BETTER_SLIDES.length}
                        activeIndex={selectedIndex}
                        onSelect={scrollTo}
                        autoplayActive={autoplayActive}
                    />
                </div>
            </div>

            <GetICMAppModal open={showAppModal} onClose={() => setShowAppModal(false)} />
        </section>
    );
};

export default KnowMoneyBetter;
